import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtDateTimeIST, fmtTimeIST } from "../lib/datefmt";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Select, Label, EmptyState } from "../components/ui-kit";
import { MapPin, Play, Square, Camera } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import { LazyPhoto } from "../components/LazyPhoto";

// Fix leaflet default marker icons (CDN)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function GPSPage() {
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    const [a, s] = await Promise.all([api.get("/gps/day/active"), api.get("/gps/sessions")]);
    setActiveSession(a.data || null);
    setSessions(s.data || []);
    if (!selectedId && s.data.length) {
      setSelectedId(s.data[0].id);
    }
  };

  const loadSelected = async (id) => {
    if (!id) return;
    const { data } = await api.get(`/gps/sessions/${id}/points`);
    setSelectedMeta(data.session);
    setSelectedPoints(data.points);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (selectedId) loadSelected(selectedId);
  }, [selectedId]);

  // Polling: every 5 min while active — uses geolocation
  useEffect(() => {
    if (!activeSession) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const tick = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await api.post("/gps/points", { lat: pos.coords.latitude, lng: pos.coords.longitude });
            if (selectedId === activeSession.id) loadSelected(activeSession.id);
          } catch (err) {
            logger.error("GPS point post failed:", err);
          }
        },
        (err) => logger.warn("Geolocation refused:", err?.message || err),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    };
    // Immediate + every 5 minutes
    tick();
    pollRef.current = setInterval(tick, 5 * 60 * 1000);
    return () => pollRef.current && clearInterval(pollRef.current);
    // Re-run only when the active session changes (start/end day). `selectedId`
    // and `loadSelected` are read fresh inside `tick` so we don't need them as deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession?.id]);

  const startDay = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not available");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/gps/day/start", { lat: pos.coords.latitude, lng: pos.coords.longitude });
          toast.success("Day started — tracking every 5 min");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
      },
      async () => {
        // still start without initial coords
        try {
          await api.post("/gps/day/start", {});
          toast.success("Day started");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
      }
    );
  };

  const endDay = async () => {
    try {
      await api.post("/gps/day/end");
      toast.success("Day ended");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const checkIn = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      await api.post("/gps/points", { lat: pos.coords.latitude, lng: pos.coords.longitude, note: "Manual check-in" });
      toast.success("Location captured");
      if (selectedId) loadSelected(selectedId);
    });
  };

  // Read a File, resize to max 1024px on the long edge, return base64 JPEG (~70% quality).
  // Keeps GPS payloads small while preserving enough detail to verify a visit.
  const fileToCompressedB64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error("Could not read image"));
        img.onload = () => {
          const maxEdge = 1024;
          const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const checkInWithPhoto = async (file) => {
    if (!file) return;
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    const tId = toast.loading("Capturing location & photo…");
    try {
      const photo_b64 = await fileToCompressedB64(file);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await api.post("/gps/points", {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              photo_b64,
              note: "Photo check-in",
            });
            toast.success("Photo check-in saved", { id: tId });
            if (selectedId) loadSelected(selectedId);
          } catch (err) {
            logger.error("photo checkin save failed:", err);
            toast.error(formatApiErrorDetail(err?.response?.data?.detail), { id: tId });
          }
        },
        (err) => {
          logger.warn("Geolocation refused:", err?.message || err);
          toast.error("Location permission denied", { id: tId });
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    } catch (err) {
      logger.error("photo read failed:", err);
      toast.error("Could not read photo", { id: tId });
    }
  };

  const polyline = selectedPoints.map((p) => [p.lat, p.lng]);
  const center = polyline.length > 0 ? polyline[Math.floor(polyline.length / 2)] : [20.5937, 78.9629];

  return (
    <div>
      <PageHeader
        title="GPS Tracking"
        subtitle="Start your day to auto-capture your field route every 5 minutes."
        actions={
          activeSession ? (
            <>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-transparent text-[#1E3F20] border border-[#1E3F20] hover:bg-[#F2EDE4] rounded-lg px-4 py-2 font-semibold text-sm transition-colors">
                <Camera className="w-4 h-4" /> Photo check-in
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="gps-photo-checkin"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) checkInWithPhoto(f);
                    e.target.value = "";
                  }}
                />
              </label>
              <Button variant="outline" onClick={checkIn} data-testid="gps-checkin">
                <MapPin className="w-4 h-4" /> Quick check-in
              </Button>
              <Button variant="secondary" onClick={endDay} data-testid="gps-end">
                <Square className="w-4 h-4" /> End day
              </Button>
            </>
          ) : (
            <Button onClick={startDay} data-testid="gps-start">
              <Play className="w-4 h-4" /> Start day
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-0 overflow-hidden">
          {polyline.length === 0 ? (
            <div className="p-10">
              <EmptyState title="No route to show" description="Start your day, move around, and come back to see your full route timeline." />
            </div>
          ) : (
            <div className="h-[420px]">
              <MapContainer key={selectedId || "empty"} center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Polyline positions={polyline} pathOptions={{ color: "#1E3F20", weight: 4 }} />
                {selectedPoints.map((p, i) => (
                  <Marker position={[p.lat, p.lng]} key={p.id}>
                    <Popup minWidth={180}>
                      <div className="text-xs">
                        <div className="font-bold">Point #{i + 1}</div>
                        <div>{fmtDateTimeIST(p.timestamp)}</div>
                        {p.note && <div className="mt-1">{p.note}</div>}
                        {(p.has_photo || p.photo_b64) && (
                          <LazyPhoto
                            kind="gps_point"
                            id={p.id}
                            alt="Check-in"
                            className="mt-2 w-[200px] h-[120px] rounded border border-[#E5E2DC]"
                            onOpen={(url) => window.open(url, "_blank", "noreferrer")}
                          />
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </Card>

        <Card>
          <Label>Session</Label>
          <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="gps-session-select">
            <option value="">Select a session…</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {fmtDateTimeIST(s.start_time)} — {s.status}
                {s.user_name ? ` — ${s.user_name}` : ""}
              </option>
            ))}
          </Select>
          {selectedMeta && (
            <div className="mt-4 text-sm space-y-1">
              <div className="flex items-center gap-2 text-[#1E3F20]">
                <MapPin className="w-4 h-4" />
                <span className="font-semibold">{selectedMeta.user_name || "Me"}</span>
              </div>
              <div className="text-[#5C6B5E]">
                Start: {fmtDateTimeIST(selectedMeta.start_time)}
              </div>
              {selectedMeta.end_time && (
                <div className="text-[#5C6B5E]">End: {fmtDateTimeIST(selectedMeta.end_time)}</div>
              )}
              <div className="pt-2 text-xs uppercase tracking-widest text-[#5C6B5E]">Timeline</div>
              <ul className="mt-2 max-h-72 overflow-y-auto divide-y divide-[#E5E2DC] text-xs">
                {selectedPoints.map((p, i) => (
                  <li key={p.id} className="py-2">
                    <div className="flex items-start gap-2">
                      {(p.has_photo || p.photo_b64) && (
                        <LazyPhoto
                          kind="gps_point"
                          id={p.id}
                          alt="Check-in"
                          className="flex-shrink-0 w-12 h-12 rounded border border-[#E5E2DC]"
                          onOpen={(url) => window.open(url, "_blank", "noreferrer")}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">
                          #{i + 1} — {fmtTimeIST(p.timestamp)}
                        </div>
                        <div className="text-[#5C6B5E]">
                          {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                        </div>
                        {p.note && <div className="text-[#5C6B5E]">{p.note}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
